import React from "react";
import FullLoader from "../Loader/FullLoader";

const DIAGNOSTIC_PROPS = [
    "FD Alert State",
    "FD Alert Value",
    "ShCtRangesAdc",
    "ShCtsInstalledAdc"
];

const DIAGNOSTIC_FAULT_STR = "ShFault";
const DIAGSNOSTIC_FAULTS = [
    "ShFault1",
    "ShFault2",
    "ShFault3",
    "ShFault4",
    "ShFault5",
    "ShFault6",
    "ShFault7",
    "ShFault8",
    "ShFault9",
    "ShFault10",
    "ShFault11",
    "ShFault12"
];

const GATEWAY_REBOOT_CODES = {
    0: {
        FwRev: '',
        Problem: 'None',
        Action: 'None'
    },
    2: {
        FwRev: 'All',
        Problem: 'Device in OTA Firmware Update',
        Action: 'This reboot occurs after the gateway\'s firmware has been updated by OTA.'
    },
    3: {
        FwRev: 'All',
        Problem: 'Mobile API restorewifi.htm',
        Action: 'This is expected with some Mobile App journeys.'
    },
    4: {
        FwRev: 'All',
        Problem: 'Device rebooted through console',
        Action: 'Reboot requested through USB port. We do not expect to see this in field.'
    },
    5: {
        FwRev: 'All',
        Problem: 'Gateway failed to connect to home WiFi',
        Action: 'Indicates possible problem with homeowner wifi.'
    },
    6: {
        FwRev: 'All',
        Problem: 'Gateway disconnected from home WiFi',
        Action: 'This indicates that the gateway was commanded to disconnect through the USB port - we do not expect to see this in field.'
    },
    7: {
        FwRev: 'All',
        Problem: 'Home WiFi did not respond to gateway DHCP request',
        Action: 'DHCP is used to set the gateway\'s IP address and to set the DNS server IP addresses. This most likely indicates a problem with homeowner network or ISP.'
    },
    8: {
        FwRev: 'All',
        Problem: 'Gateway could not get time update from NTP server',
        Action: 'When the gateway first boots up it does not know the time of day.  It must retrieve the time of day from an NTP server in order to securely communicate with our servers. Note that firmware versions 2.5 and newer can also obtain time from the OTA server. For these versions, GatewayRebootCode=8 indicates that both the NTP and OTA server time updates failed.'
    },
    9: {
        FwRev: 'All',
        Problem: 'Gateway server configuration failed',
        Action: 'This indicates a firmware bug. It is not expected but should be reported to engineering if it occurs.'
    },
    13: {
        FwRev: 'All',
        Problem: 'Gateway was unable to send data to TwinThread 10 consecutive times',
        Action: 'This most likely indicates a problem with the customer\'s Internet connection or TwinThread.'
    },
    14: {
        FwRev: 'All',
        Problem: 'Communications failure while communicating with TwinThread',
        Action: 'This most likely indicates a problem with the customer\'s Internet connection or TwinThread.'
    },
    15: {
        FwRev: 'All',
        Problem: 'Mobile application changed WiFi configuration.',
        Action: 'This is expected with some Mobile App journeys.'
    },
    16: {
        FwRev: 'All',
        Problem: 'No communications with any Sensor Hub for 90 minutes',
        Action: 'Check furnace power and furnace sensor hub cables.'
    },
    17: {
        FwRev: 'All',
        Problem: 'Device rebooted through system console',
        Action: 'Reboot requested through USB port. We do not expect to see this in field.'
    },
    18: {
        FwRev: 'All',
        Problem: 'TwinThread access key is out of date and gateway did not received an updated key from OTA server',
        Action: 'The access key is updated periodically and a new version is obtained from the OTA server. This error could indicate that the OTA server has not been updated with the new access key, or that the OTA server is mis-configured.'
    },
    19: {
        FwRev: '2.5',
        Problem: 'Device rebooted due to error in WiFi driver',
        Action: 'The Gateway WiFi driver includes a bug that requires a reboot to recover from. We have not observed this problem in the field, but this code could be logged on occasion.'
    },
    20: {
        FwRev: '2.5',
        Problem: 'Device in Power cycle',
        Action: 'Prior to rev 2.5, power cycle could be inferred by GatewayResets and GatewayRebootOverflow going to 0.'
    },
    22: {
        FwRev: '2.5',
        Problem: 'Device performed Watchdog Reset',
        Action: 'A watchdog reset indicates a possible problem in the gateway firmware. Any occurrence of this code should be reported to engineering.'
    }
}

const SH_FAULTS = {
    'ShFault1': {
        Description: "Number of times sensor has booted.  Maintained by boot loader.",
        NVM: true
    },
    'ShFault2': {
        Description: 'Number of application code startups.',
        NVM: true
    },
    'ShFault3': {
        Description: 'Number of software watchdog resets.  Software watchdog occurs in following cases:\n' +
            '1.\tCannot connect to gateway on startup.\n' +
            '2.\tCannot communicate to gateway for 15 minutes.\n',
        NVM: true
    },
    'ShFault4': {
        Description: 'Number of transmitted packets that timed out (since application startup) without an acknowledge from gateway.',
        NVM: false
    },
    'ShFault5': {
        Description: 'Number of CT samples discarded (since application startup) because there was no space in sample FIFO.',
        NVM: false
    },
    'ShFault6': {
        Description: 'Number of power on resets. This indicates number of times sensor has powered up. For outdoor sensor, device had completely powered down.',
        NVM: true
    },
    'ShFault7': {
        Description: 'Number of hardware watchdog resets.  Indicates processor was busy and failed to reset hardware watchdog timer.\n' +
            'There is one intentional watchdog timeout in code on timeout of OTA Announce message.\n',
        NVM: true
    },
    'ShFault8': {
        Description: 'Number of software resets.  A software reset (SWR) is requested by the application code.  This can be due to the following occurrences:\n' +
            '•\tSoftware watchdog (see SW_WATCHDOG_CNT)\n' +
            '•\tProcessor exception (see EXCEPTION_CNT, etc.)\n' +
            '•\tOne software reset occurs at the factory at the end of manufacturing test\n' +
            '•\tAfter OTA image has been received a SWR is performed in order to go back to bootloader to verify and program image\n',
        NVM: true
    },
    'ShFault9': {
        Description: 'Number of resets caused by pressing RESET pushbutton (paperclip) plus POR count (ShFault6).',
        NVM: true
    },
    'ShFault10': {
        Description: 'Number of processor exceptions.',
        NVM: true
    },
    'ShFault11': {
        Description: 'Exception code.',
        NVM: true
    },
    'ShFault12': {
        Description: 'Address of exception.  Convert to hexadecimal and extract bottom 32 bits.  For example:\n' +
            '•\tTwinThread reports -1,660,855,871\n' +
            '•\tConvert to hex FFFF FFFF 9D01 59C1\n' +
            '•\tException address is 9D0159C1.\n' +
            'Note that not all exceptions provide accurate addresses.\n',
        NVM: true
    }
}

function DiagnosticsModal({ show, onClick, properties, gatewayRebootCodes, loading }) {
    let diagnosticProps = properties !== null && properties !== undefined ? properties.filter(p => !DIAGSNOSTIC_FAULTS.includes(p.name) && DIAGNOSTIC_PROPS.includes(p.name)) : [];
    let shFaultProps = properties !== null && properties !== undefined ? properties.filter(p => DIAGSNOSTIC_FAULTS.includes(p.name) || p.name.includes(DIAGNOSTIC_FAULT_STR)) : [];

    let loader = loading ?
        <FullLoader/>
        : <div>{null}</div>;
    
    return (
        <div className="modal" style={{display: show ? "block" : "none"}}>
            <div className="modal-wrapper"
                 style={{
                     transform: show ? 'translateY(0vh)' : 'translateY(-100vh)',
                     opacity: show ? '1' : '0'
                 }}>

                <div className="modal-header">
                    <h3>Device Diagnostics</h3>
                    <span className="close-modal-btn" onClick={() => onClick()}>×</span>
                </div>

                <div className={"margin-0-20"}>
                    <h5 className={"margin-top-10"}>Gateway Diagnostics</h5>
                    <table className={"width-100p margin-bottom-0"}>

                        <thead>
                            <tr className="alerts-table-row">
                                <th className="table-shrink text-center">
                                    REBOOT CODE
                                </th>
                                <th className="table-shrink">
                                    PROBLEM
                                </th>
                                <th className="table-shrink">
                                    ACTION
                                </th>
                                <th className="table-shrink text-center">
                                    FIRMWARE REV.
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {gatewayRebootCodes?.map((p, i) => {
                                return (<tr className="alerts-table-row" key={`fault-${i}`}>
                                    <td className={"border-left text-center"}>{p}</td>
                                    <td>{GATEWAY_REBOOT_CODES[p]?.Problem}</td>
                                    <td>{GATEWAY_REBOOT_CODES[p]?.Action}</td>
                                    <td className={"border-right text-center"}>{GATEWAY_REBOOT_CODES[p]?.FwRev}</td>
                                </tr>)
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={"margin-0-20"}>
                    <h5 className={"margin-top-25"}>Sensor Hub Diagnostics</h5>
                    <table className={"width-100p margin-bottom-0"}>

                        <thead>
                            <tr className="alerts-table-row">
                                <th className="table-shrink text-center">
                                    FAULT
                                </th>
                                <th className="table-shrink">
                                    VALUE
                                </th>
                                <th className="table-shrink width-300">
                                    DESCRIPTION
                                </th>
                                <th className="table-shrink text-center">
                                    NVM?
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {DIAGSNOSTIC_FAULTS?.map((p, i) => {
                                return (<tr className="alerts-table-row" key={`fault-${i}`}>
                                    <td className={"border-left text-center"}>{p}</td>
                                    <td>{shFaultProps?.find(s => s.name === p)?.value}</td>
                                    <td>{SH_FAULTS[p]?.Description}</td>
                                    <td className={"border-right text-center"}>{SH_FAULTS[p]?.NVM ? "Y" : "n"}</td>
                                </tr>)
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={"margin-0-20"}>
                    <h5 className={"margin-top-25"}>Other Diagnostic Properties</h5>
                    <table className={"width-100p margin-bottom-0"}>

                        <thead>
                        <tr className="alerts-table-row">
                            <th className="table-shrink text-center">
                                PROPERTY
                            </th>
                            <th className="table-shrink text-center">
                                VALUE
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {diagnosticProps?.map((p, i) => {
                            return (<tr className="alerts-table-row" key={`prop-${i}`}>
                                <td className={"border-left text-center"}>{p.name}</td>
                                <td className={"border-right text-center"}>{p.value}</td>
                            </tr>)
                        })}
                        </tbody>
                    </table>
                    <br/>
                </div>
                {loader}
            </div>
        </div>
    );
}

export default DiagnosticsModal;